'use client';

/**
 * CliTerminal — an xterm.js terminal bound to a CLI-sandbox WebSocket. Shared primitive
 * (Rule 33): lives in @inferrex/ui so every host frontend (platform, regulated/sovereign)
 * renders the same interactive shell. Self-contained — props are primitive (`sessionId`,
 * `wsBaseUrl`, callbacks); colours resolve from the host's design tokens at mount.
 */

import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { OPEN_URL_OSC, findUrls, isWebUrl, openUrlFromOsc } from './cli-terminal-links.js';

/** Open a link from terminal output in a new tab, never with access back to this page. */
function openExternal(url: string): void {
  if (isWebUrl(url)) window.open(url, '_blank', 'noopener,noreferrer');
}

export interface CliTerminalProps {
  readonly sessionId: string;
  readonly wsBaseUrl: string;
  /**
   * Resolves the caller's access token. The gateway authenticates the WS upgrade (it bypasses the
   * HTTP auth middleware), and the browser WebSocket API can't set headers — so the token rides in
   * `?token=` (the same convention the SSE client uses). Omit only for an unauthenticated host.
   */
  readonly getToken?: () => Promise<string | null>;
  readonly onDisconnect?: () => void;
  readonly onData?: (data: string) => void;
  /**
   * A CLI in the sandbox asked to open a browser (its login URL). The terminal prints the URL
   * as a clickable link either way; this lets the host page surface it as well.
   */
  readonly onOpenUrl?: (url: string) => void;
}

/**
 * Imperative handle so a host can run a command in the session the user is
 * already in — a saved snippet or a provider's declared auth command — without
 * opening a second shell or asking them to retype it.
 */
export interface CliTerminalHandle {
  /**
   * Write text into the PTY exactly as if it had been typed. Include a trailing
   * "\r" to execute; omit it to stage the command on the prompt so the user can
   * read and edit it before pressing enter (the safer default for anything that
   * touches credentials).
   *
   * No-op when the socket is not open, so an action fired at a disconnected
   * terminal is ignored rather than silently lost mid-stream.
   */
  send(data: string): void;
}

export const CliTerminal = forwardRef<CliTerminalHandle, CliTerminalProps>(function CliTerminal(
  { sessionId, wsBaseUrl, getToken, onDisconnect, onData, onOpenUrl }: CliTerminalProps,
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const fitRef = useRef<FitAddon | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;

    // Resolve design tokens at mount time — xterm needs concrete colour values. The --terminal-*
    // tokens are always defined in @inferrex/ui tokens.css, so no hardcoded colour fallback is
    // needed (and none is allowed — the css-token validator enforces it).
    const root = getComputedStyle(document.documentElement);
    const cssToken = (name: string) => root.getPropertyValue(name).trim();

    const term = new Terminal({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: cssToken('--font-mono') || 'JetBrains Mono, Menlo, Monaco, monospace',
      theme: {
        background: cssToken('--terminal-bg'),
        foreground: cssToken('--terminal-text'),
        cursor: cssToken('--terminal-cursor'),
        selectionBackground: cssToken('--terminal-selection'),
      },
      convertEol: true,
      // OSC 8 hyperlinks (what the sandbox's browser shim prints) open on click.
      linkHandler: { activate: (_event, uri) => openExternal(uri) },
    });

    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(containerRef.current);
    fit.fit();
    termRef.current = term;
    fitRef.current = fit;

    // Plain URLs in the output (a device-code page, an auth link) are clickable too.
    const linkProvider = term.registerLinkProvider({
      provideLinks(y, callback) {
        const text = term.buffer.active.getLine(y - 1)?.translateToString(true) ?? '';
        const links = findUrls(text).map((m) => ({
          range: { start: { x: m.start + 1, y }, end: { x: m.end, y } },
          text: m.url,
          activate: (_event: MouseEvent, url: string) => openExternal(url),
        }));
        callback(links.length > 0 ? links : undefined);
      },
    });
    // The shim's "open this URL" sequence: handed to the host; the shim also prints the link.
    const oscHandler = term.parser.registerOscHandler(OPEN_URL_OSC, (data) => {
      const url = openUrlFromOsc(data);
      if (url) onOpenUrl?.(url);
      return true;
    });

    // Protocol (services/cli-sandbox/src/terminal-protocol.ts): input as BINARY frames, control
    // as JSON TEXT frames. The size goes on the URL so the shell starts at the right width.
    const encoder = new TextEncoder();
    const sendInput = (ws: WebSocket, data: string): void => {
      if (ws.readyState === WebSocket.OPEN) ws.send(encoder.encode(data));
    };

    // Acquire the access token, then open the AUTHENTICATED socket — the gateway validates `?token=`
    // on upgrade and forwards the tenant to the internal PTY.
    void (async () => {
      const accessToken = getToken ? await getToken() : null;
      if (cancelled || termRef.current !== term) return;
      const params = new URLSearchParams({ cols: String(term.cols), rows: String(term.rows) });
      if (accessToken) params.set('token', accessToken);
      const ws = new WebSocket(`${wsBaseUrl}/ws/${sessionId}?${params.toString()}`);
      wsRef.current = ws;

      ws.onopen = () => {
        term.writeln('\x1b[32m✓ Connected to CLI sandbox\x1b[0m');
        term.writeln('');
      };
      ws.onmessage = (event) => {
        const data = typeof event.data === 'string' ? event.data : '';
        term.write(data);
        onData?.(data);
      };
      ws.onclose = (event) => {
        term.writeln('');
        term.writeln(`\x1b[33m⚠ Disconnected (${event.reason || 'session ended'})\x1b[0m`);
        onDisconnect?.();
      };
      ws.onerror = () => {
        term.writeln('\x1b[31m✗ Connection error\x1b[0m');
      };
      term.onData((data) => sendInput(ws, data));
      term.onResize(({ cols, rows }) => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'resize', cols, rows }));
      });
    })();

    // Refit when the container changes size (window resize, dock drawer drag), which in turn
    // sends the new size to the shell through onResize above.
    const handleResize = () => { fitRef.current?.fit(); };
    window.addEventListener('resize', handleResize);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(handleResize);
    observer?.observe(containerRef.current);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', handleResize);
      observer?.disconnect();
      linkProvider.dispose();
      oscHandler.dispose();
      wsRef.current?.close();
      termRef.current?.dispose();
    };
  }, [sessionId, wsBaseUrl, getToken, onDisconnect, onData, onOpenUrl]);

  // Deliberately the SAME framing a keystroke takes (binary, as term.onData sends), so a
  // programmatic command cannot diverge from typed input in framing or ordering.
  useImperativeHandle(ref, () => ({
    send(data: string) {
      const ws = wsRef.current;
      if (ws?.readyState === WebSocket.OPEN) ws.send(new TextEncoder().encode(data));
    },
  }), []);

  return (
    <div
      ref={containerRef}
      className="w-full h-100 rounded-lg overflow-hidden border border-border bg-terminal"
    />
  );
});
